import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { fetchToolResume } from "@/lib/api";
import { track } from "@/lib/analytics";

/**
 * Hook for tool pages: if the URL contains `?resume=<token>`, fetch the
 * stored payload and call `onRestore({ inputs, result, zipCode })`. Toasts a
 * confirmation when restore succeeds. Idempotent — guarded by a ref so it
 * only runs once per token even under StrictMode double-effects.
 *
 * The page is responsible for actually applying the restored state to its
 * useState setters inside the `onRestore` callback.
 */
export function useResumeFromUrl(toolSlug, onRestore) {
    const [params, setParams] = useSearchParams();
    const consumedRef = useRef(null);

    useEffect(() => {
        const token = params.get("resume");
        if (!token || consumedRef.current === token) return;
        consumedRef.current = token;
        // Capture UTM tags BEFORE we strip them from the URL so PostHog can
        // attribute email-driven recoveries vs direct link opens.
        const utmSource = params.get("utm_source");
        const utmCampaign = params.get("utm_campaign");
        const recoveredFrom = utmSource === "email" ? "email" : "direct";
        (async () => {
            try {
                const r = await fetchToolResume(token, {
                    utm_source: utmSource,
                    utm_medium: params.get("utm_medium"),
                    utm_campaign: utmCampaign,
                });
                if (r?.tool_name !== toolSlug) {
                    toast.error("This resume link is for a different tool.");
                    return;
                }
                onRestore?.({
                    inputs: r.inputs || {},
                    result: r.result || null,
                    zipCode: r.zip_code || null,
                });
                // Two events:
                //   tool_resume_restored — local "your answers came back" signal
                //   tool_resume_recovered — funnel-level recovery (mirrors the
                //     backend tool_resume_recoveries DB row 1:1 so PostHog
                //     funnels match server-side aggregates).
                track("tool_resume_restored", { tool_name: toolSlug });
                track("tool_resume_recovered", {
                    tool_name: toolSlug,
                    recovered_from: recoveredFrom,
                    utm_source: utmSource || null,
                    utm_campaign: utmCampaign || null,
                });
                toast.success("Your answers were restored.");
                // Clean the URL so the token + UTM tags don't end up in
                // screenshots, history, or shared links.
                const next = new URLSearchParams(params);
                next.delete("resume");
                next.delete("utm_source");
                next.delete("utm_medium");
                next.delete("utm_campaign");
                setParams(next, { replace: true });
            } catch (e) {
                const status = e?.response?.status;
                if (status === 410)
                    toast.error("This resume link has expired.");
                else if (status === 404)
                    toast.error("Resume link not found.");
                else toast.error("Could not restore your answers.");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.get("resume"), toolSlug]);
}
